(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.PMSteelPartsFitment = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function normalizeIdentifier(value) {
    return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '');
  }

  function canonicalIdentifier(value) {
    return String(value || '').trim().toUpperCase();
  }

  // Exact historical model identities remain searchable after their sellable
  // product records are drafted. Values are existing verified family targets;
  // this registry does not create or infer any compatibility edge.
  var historicalModelIdentityFamilies = {
    'PS-203': [
      'PS-203-PK',
      'PS-203-TQ',
      'PS-203-RD',
      'PS-203-MT',
      'PS-203-BL',
      'PS-203-BK',
      'PS-203-WT',
    ],
    'PS-203F1': [
      'PS-203F1-TP',
      'PS-203F1-TL',
      'PS-203F1-SA',
      'PS-203F1-RD',
      'PS-203F1-CR',
      'PS-203F1-BK',
    ],
    'PS-223': [
      'PS-223-TL',
      'PS-223-SS',
      'PS-223-AZ',
      'PS-223-BK',
      'PS-223-WT',
    ],
  };
  var historicalModelIdentityLookup = {};
  Object.keys(historicalModelIdentityFamilies).forEach(function (family) {
    historicalModelIdentityFamilies[family].forEach(function (identifier) {
      historicalModelIdentityLookup[normalizeIdentifier(identifier)] = family;
    });
  });

  function historicalModelStem(query) {
    return historicalModelIdentityLookup[normalizeIdentifier(query)] || '';
  }

  function deriveModelStem(skus) {
    var values = (skus || []).map(canonicalIdentifier).filter(Boolean);
    if (!values.length) return '';
    if (values.length === 1) return values[0];

    var tokenSets = values.map(function (sku) { return sku.split('-'); });
    var shared = [];
    for (var i = 0; i < tokenSets[0].length; i += 1) {
      var token = tokenSets[0][i];
      if (tokenSets.every(function (tokens) { return tokens[i] === token; })) shared.push(token);
      else break;
    }
    return shared.length ? shared.join('-') : '';
  }

  function modelEntries(models) {
    var entries = [];
    (models || []).forEach(function (model) {
      var stem = canonicalIdentifier(model.stem || deriveModelStem(model.skus));
      var identifiers = [];
      if (stem) identifiers.push(stem);
      (model.skus || []).forEach(function (sku) {
        var value = canonicalIdentifier(sku);
        if (value && identifiers.indexOf(value) === -1) identifiers.push(value);
      });
      entries.push({ model: model, stem: stem, identifiers: identifiers });
    });
    return entries;
  }

  function findExactModel(query, models) {
    var wanted = normalizeIdentifier(query);
    if (!wanted) return null;
    var matches = [];
    modelEntries(models).forEach(function (entry) {
      entry.identifiers.forEach(function (identifier) {
        if (normalizeIdentifier(identifier) === wanted) {
          matches.push({ entry: entry, identifier: identifier });
        }
      });
    });
    if (matches.length !== 1) return null;
    var hit = matches[0];
    var targets = [hit.identifier];
    if (hit.entry.stem && normalizeIdentifier(hit.entry.stem) !== normalizeIdentifier(hit.identifier)) {
      targets.push(hit.entry.stem);
    }
    return {
      model: hit.entry.model,
      identifier: hit.identifier,
      stem: hit.entry.stem,
      fitTargets: targets,
    };
  }

  function fitTargetSet(part) {
    var set = {};
    (part.tags || []).forEach(function (tag) {
      if (String(tag).slice(0, 5).toLowerCase() === 'fits:') {
        set[normalizeIdentifier(String(tag).slice(5))] = true;
      }
    });
    return set;
  }

  function partMatchesTargets(part, targets) {
    var actual = fitTargetSet(part);
    return (targets || []).some(function (target) { return actual[normalizeIdentifier(target)] === true; });
  }

  function looksLikeIdentifier(query) {
    var value = String(query || '').trim();
    return /[A-Za-z]/.test(value) && /[0-9]/.test(value) && /^[A-Za-z0-9_. -]+$/.test(value);
  }

  function prefixIdentifierMatch(query, identifier) {
    var rawQuery = String(query || '').trim().toUpperCase();
    var querySegments = rawQuery.split(/[-_. ]+/).filter(Boolean);
    var identifierSegments = String(identifier || '').trim().toUpperCase().split(/[-_. ]+/).filter(Boolean);
    if (!querySegments.length || querySegments.length > identifierSegments.length) return false;
    var completedLastSegment = /[-_. ]$/.test(rawQuery);
    return querySegments.every(function (segment, index) {
      var candidate = identifierSegments[index];
      return index === querySegments.length - 1 && !completedLastSegment
        ? candidate.indexOf(segment) === 0
        : candidate === segment;
    });
  }

  function levenshtein(a, b) {
    var previous = [];
    var current = [];
    var i;
    var j;
    for (j = 0; j <= b.length; j += 1) previous[j] = j;
    for (i = 1; i <= a.length; i += 1) {
      current[0] = i;
      for (j = 1; j <= b.length; j += 1) {
        current[j] = Math.min(
          previous[j] + 1,
          current[j - 1] + 1,
          previous[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1)
        );
      }
      previous = current.slice();
    }
    return previous[b.length];
  }

  function fuzzyTokenHit(queryToken, blobToken) {
    if (blobToken.indexOf(queryToken) > -1) return true;
    if (queryToken.length >= 4 && blobToken.length >= 3 && levenshtein(queryToken, blobToken) <= 1) return true;
    if (queryToken.length >= 6 && blobToken.length >= 4 && levenshtein(queryToken, blobToken) <= 2) return true;
    return false;
  }

  function buildPartBlob(part, models, includeTargets) {
    var values = [part.title || '', part.sku || ''];
    var entries = modelEntries(models);
    (part.tags || []).forEach(function (tag) {
      if (String(tag).slice(0, 5).toLowerCase() !== 'fits:') return;
      var target = String(tag).slice(5);
      if (includeTargets !== false) values.push(target);
      var normalized = normalizeIdentifier(target);
      entries.forEach(function (entry) {
        if (entry.identifiers.some(function (identifier) { return normalizeIdentifier(identifier) === normalized; })) {
          values.push(entry.model.title || '');
        }
      });
    });
    return values.join('\u0000');
  }

  function fuzzyScore(part, models, query) {
    var normalizedQuery = String(query || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    var normalizedBlob = buildPartBlob(part, models).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    var compactQuery = normalizedQuery.replace(/[^a-z0-9]+/g, '');
    var compactBlob = normalizedBlob.replace(/[^a-z0-9]+/g, '');
    if (compactQuery && compactBlob.indexOf(compactQuery) > -1) return 100;

    var queryTokens = normalizedQuery.split(' ').filter(Boolean);
    var blobTokens = normalizedBlob.split(' ').filter(Boolean);
    var matched = queryTokens.filter(function (queryToken) {
      return blobTokens.some(function (blobToken) { return fuzzyTokenHit(queryToken, blobToken); });
    }).length;
    return queryTokens.length && matched === queryTokens.length ? 10 + matched : 0;
  }

  function searchParts(parts, models, query, categoryTag) {
    query = String(query || '').replace(/[\u2010-\u2015\u2212]/g, '-').replace(/\u00a0/g, ' ');
    var candidates = (parts || []).filter(function (part) {
      return !categoryTag || (part.tags || []).indexOf(categoryTag) > -1;
    });
    var wanted = normalizeIdentifier(query);
    if (!wanted) return [];

    var exactPartRows = candidates.filter(function (part) {
      var skus = part.skus || [part.sku];
      return skus.some(function (sku) { return normalizeIdentifier(sku) === wanted; });
    });
    if (exactPartRows.length) return exactPartRows;

    var modelMatch = findExactModel(query, models);
    if (modelMatch) {
      return candidates.filter(function (part) { return partMatchesTargets(part, modelMatch.fitTargets); });
    }

    var historicalStem = historicalModelStem(query);
    if (historicalStem) {
      return candidates.filter(function (part) {
        return partMatchesTargets(part, [query, historicalStem]);
      });
    }

    var exactExternalRows = candidates.filter(function (part) {
      return fitTargetSet(part)[wanted] === true;
    });
    if (exactExternalRows.length) return exactExternalRows;

    if (wanted.length >= 3) {
      var prefixSkuRows = candidates.filter(function (part) {
        var skus = part.skus || [part.sku];
        return skus.some(function (sku) { return prefixIdentifierMatch(query, sku); });
      });
      var prefixFitRows = candidates.filter(function (part) {
        if (prefixSkuRows.indexOf(part) !== -1) return false;
        return (part.tags || []).some(function (tag) {
          return String(tag).slice(0, 5).toLowerCase() === 'fits:' &&
            prefixIdentifierMatch(query, String(tag).slice(5));
        });
      });
      if (prefixSkuRows.length || prefixFitRows.length) return prefixSkuRows.concat(prefixFitRows);
    }

    if (looksLikeIdentifier(query)) return /^\s*[0-9]/.test(query) ? candidates.filter(function (part) { var nq = normalizeIdentifier(query).toLowerCase(); if (nq.length > 80) return false; var pattern = '(^|[^a-z0-9])' + nq.match(/[0-9]+|[a-z]+/g).join('[^a-z0-9\u0000]*') + '([^a-z0-9]|$)'; return new RegExp(pattern).test(buildPartBlob(part, models, false).toLowerCase()); }) : [];

    return candidates.map(function (part, index) {
      return { part: part, index: index, score: fuzzyScore(part, models, query) };
    }).filter(function (row) {
      return row.score > 0;
    }).sort(function (a, b) {
      return b.score - a.score || a.index - b.index;
    }).map(function (row) {
      return row.part;
    });
  }

  function getModelFitResult(parts, model, categoryTag) {
    var stem = canonicalIdentifier(model && (model.stem || deriveModelStem(model.skus)));
    if (!stem) return { verified: false, rows: [] };
    var rows = (parts || []).filter(function (part) {
      if (categoryTag && (part.tags || []).indexOf(categoryTag) === -1) return false;
      return partMatchesTargets(part, [stem]);
    });
    return { verified: rows.length > 0, rows: rows };
  }

  function validateManifestDocument(manifest, livePartSkus) {
    var errors = [];
    var allowed = { 'verified-fit': true, 'not-model-specific': true, deferred: true };
    var manifestCounts = {};
    var liveCounts = {};

    if (!manifest || manifest.version !== 1 || !Array.isArray(manifest.parts)) {
      return { errors: ['manifest must be version 1 with a parts array'] };
    }

    var externalCounts = {};
    (manifest.externalModels || []).forEach(function (target) {
      var canonical = canonicalIdentifier(target);
      if (!/^[A-Z0-9]+(?:-[A-Z0-9]+)*$/.test(canonical) && !/^146\.\d{8}$/.test(canonical)) {
        errors.push('non-canonical external model target: ' + target);
      }
      externalCounts[canonical] = (externalCounts[canonical] || 0) + 1;
      if (externalCounts[canonical] > 1) errors.push('duplicate external model target: ' + canonical);
    });

    manifest.parts.forEach(function (part) {
      var sku = canonicalIdentifier(part.sku);
      manifestCounts[sku] = (manifestCounts[sku] || 0) + 1;
      if (manifestCounts[sku] > 1) errors.push('duplicate manifest Part SKU: ' + sku);
      if (!allowed[part.classification]) errors.push('invalid classification for ' + sku + ': ' + part.classification);
      if (!Array.isArray(part.fits) || !Array.isArray(part.sourceFits)) {
        errors.push('fits and sourceFits must be arrays for ' + sku);
        return;
      }
      if (part.classification === 'verified-fit' && part.fits.length === 0) {
        errors.push('verified-fit has no fit targets: ' + sku);
      }
      if (part.classification !== 'verified-fit' && part.fits.length > 0) {
        errors.push(part.classification + ' must not emit fit targets: ' + sku);
      }
      var targetCounts = {};
      part.fits.forEach(function (target) {
        var canonical = canonicalIdentifier(target);
        if (!/^[A-Z0-9]+(?:-[A-Z0-9]+)*$/.test(canonical) && !/^146\.\d{8}$/.test(canonical)) {
          errors.push('non-canonical fit target for ' + sku + ': ' + target);
        }
        var normalized = normalizeIdentifier(canonical);
        targetCounts[normalized] = (targetCounts[normalized] || 0) + 1;
        if (targetCounts[normalized] > 1) errors.push('duplicate normalized fit target for ' + sku + ': ' + target);
      });
    });

    (livePartSkus || []).forEach(function (sku) {
      var canonical = canonicalIdentifier(sku);
      liveCounts[canonical] = (liveCounts[canonical] || 0) + 1;
      if (liveCounts[canonical] > 1) errors.push('duplicate live Active Part SKU: ' + canonical);
    });

    Object.keys(liveCounts).forEach(function (sku) {
      if (!manifestCounts[sku]) errors.push('missing manifest Part SKU: ' + sku);
    });
    Object.keys(manifestCounts).forEach(function (sku) {
      if (!liveCounts[sku]) errors.push('manifest Part SKU is not live Active Part: ' + sku);
    });

    return { errors: errors };
  }

  function buildReciprocalGraph(manifest, models, partIdsBySku) {
    var graph = new Map();
    var entries = modelEntries(models);
    entries.forEach(function (entry) {
      if (entry.model.id) graph.set(entry.model.id, []);
    });

    (manifest.parts || []).forEach(function (part) {
      var partId = partIdsBySku.get(canonicalIdentifier(part.sku)) || partIdsBySku.get(part.sku);
      if (!partId) return;
      (part.fits || []).forEach(function (target) {
        var normalized = normalizeIdentifier(target);
        var matches = entries.filter(function (entry) {
          return entry.identifiers.some(function (identifier) { return normalizeIdentifier(identifier) === normalized; });
        });
        if (matches.length !== 1 || !matches[0].model.id) return;
        var list = graph.get(matches[0].model.id);
        if (list.indexOf(partId) === -1) list.push(partId);
      });
    });

    graph.forEach(function (ids) { ids.sort(); });
    return graph;
  }

  return {
    buildReciprocalGraph: buildReciprocalGraph,
    deriveModelStem: deriveModelStem,
    findExactModel: findExactModel,
    getModelFitResult: getModelFitResult,
    looksLikeIdentifier: looksLikeIdentifier,
    normalizeIdentifier: normalizeIdentifier,
    searchParts: searchParts,
    validateManifestDocument: validateManifestDocument,
  };
});
